import React from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Typography,
  Button,
  Divider,
  Tooltip,
} from "@mui/material";

import { themePalette } from "../../../constants/Theme/colors";
import ROUTES_PATH from "../../../constants/routes";
import ListItemIcon from "@mui/material/ListItemIcon";
import LockIcon from "@mui/icons-material/Lock";
import LogoutIcon from "@mui/icons-material/Logout";
import { mainSidebarItems } from "../../../constants/staticData/NavbarItems";
import { useNavigate } from "react-router";
import CommonDialog from "../../../common/commonDialog/CommonDialog";
import { useStyles } from "./style";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { authActions } from "../../../redux/slice/auth-slice";
import { useLocation } from "react-router";
import TravelRiteLogo from "../../../assets/images/LOGO-travelrite_1.svg";

import { styled } from "@mui/material/styles";

const logoutDestructiveColor = "#D92D20";
const logoutDestructiveHoverColor = "#B42318";
const logoutDestructiveBg = "#FEE4E2";

const StyledListItem = styled(ListItemButton)(({ theme }) => ({
  width: "232px", // Explicit width from design
  margin: "0 auto 12px auto", // Center horizontally, 12px bottom margin
  borderRadius: "8px",
  padding: "12px 16px",
  backgroundColor: "transparent",
  transition: "all 0.2s ease-in-out",
  "& .MuiListItemIcon-root": {
    minWidth: "38px",
    color: "#181D27",
  },
  "& .MuiTypography-root": {
    minWidth: "38px",
    color: "#181D27",
    fontFamily: "'Manrope', sans-serif",
    fontSize: "16px",
    fontWeight: 500,
    lineHeight: "100%",
    letterSpacing: "0.01em",
  },
  "&.Mui-selected": {
    backgroundColor: "#5FB92A",
    borderRadius: "12px",
    padding: "14px 20px",
    boxShadow:
      "0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "& .MuiListItemIcon-root": {
      color: "#FFFFFF",
    },
    "& .MuiTypography-root": {
      color: "#FFFFFF",
    },
    "&:hover": {
      backgroundColor: "#5FB92A",
    },
  },
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
}));

const LeftSidebar = ({ isMobile }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tab = useSelector(state => state?.auth?.defaultTab);
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClickClose = () => {
    setOpen(false);
  };

  const onSignoutClick = () => {
    handleClickClose();
    localStorage.clear();
    dispatch(authActions.resetLoggedInUser());
    navigate(ROUTES_PATH.LOG_IN);
  };

  const location = useLocation();

  return (
    <Box sx={{ height: "100vh", backgroundColor: "background.main" }}>
      <List
        component="nav"
        aria-labelledby="nested-list-subheader"
        sx={{
          height: "100%",
          width: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden"
        }}
        subheader={
          <ListSubheader
            sx={{
              p: 0, // Reset default padding
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              backgroundColor: "transparent",
              paddingLeft: "22px", // Left 22px
              paddingTop: "26px", // Top 26px
              marginBottom: "24px", // Add some space below logo
              flexShrink: 0,
            }}
            component="div"
            id="nested-list-subheader"
          >
            <img
              src={TravelRiteLogo}
              alt="TravelRite Logo"
              style={{
                width: "60px",
                height: "68px"
              }}
            />
          </ListSubheader>
        }
      >
        <Divider sx={{ borderColor: themePalette.palette.secondary.main, flexShrink: 0 }} />
        <Box
          sx={{
            display: "flex",
            pt: 5,
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            boxSizing: "border-box",
            overflowY: "auto",
            overflowX: "hidden",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(0, 0, 0, 0.2)",
              borderRadius: "3px",
              "&:hover": {
                background: "rgba(0, 0, 0, 0.3)",
              },
            },
          }}
        >
          <Box>
            {mainSidebarItems?.map((item, index) => {
              return item.label !== "Resources" &&
                item.label !== "Notification Settings" ? (
                <StyledListItem
                  selected={location.pathname.startsWith(item.path)}
                  onClick={() => {
                    navigate(`${item.path}`);
                  }}
                >
                  <ListItemIcon>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText>
                    <Typography variant="heading_regular">
                      {item.label}
                    </Typography>
                  </ListItemText>
                </StyledListItem>
              ) : (
                <Tooltip
                  title={
                    <Typography
                      fontSize={15}
                      fontFamily={"'ArchivoMedium', sans-serif"}
                    >
                      Coming Soon
                    </Typography>
                  }
                  key={item.id}
                  arrow
                  classes={{ tooltip: classes.tooltip }}
                >
                  <Box sx={{ mb: 1 }}>
                    <ListItemButton
                      sx={{
                        width: "232px",
                        margin: "0 auto",
                        borderRadius: "8px",
                        padding: "12px 16px",
                        opacity: "0.85",
                      }}
                      disabled
                    >
                      <ListItemIcon
                        sx={
                          tab === item.label
                            ? {
                              color: themePalette.palette.primary.main,
                              minWidth: "38px",
                            }
                            : {
                              color: "#181D27",
                              minWidth: "38px",
                            }
                        }
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText>
                        <Typography
                          sx={
                            tab === item.label
                              ? {
                                color: themePalette.palette.primary.main,
                                minWidth: "38px",
                              }
                              : {
                                color: "#181D27",
                                minWidth: "38px",
                              }
                          }
                          variant="heading_regular"
                        >
                          {item.label}
                        </Typography>
                      </ListItemText>
                    </ListItemButton>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>

          <Box sx={{ pt: 2, pb: 2 }}>
            <StyledListItem
              key="7"
              selected={location.pathname === "/admin/changePassword"}
              onClick={() => {
                navigate("/admin/changePassword");
              }}
            >
              <ListItemIcon>
                <LockIcon />
              </ListItemIcon>
              <ListItemText>
                <Typography variant="heading_regular">
                  Change Password
                </Typography>
              </ListItemText>
            </StyledListItem>

            <ListItemButton
              onClick={handleClickOpen}
              sx={{
                width: "232px",
                margin: "0 auto",
                borderRadius: "8px",
                padding: "12px 16px",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: "38px", color: "#181D27" }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText>
                <Typography
                  sx={{
                    color: "#181D27",
                    fontFamily: "'Manrope', sans-serif",
                    fontSize: "16px",
                    fontWeight: 500,
                    lineHeight: "100%",
                    letterSpacing: "0.01em",
                  }}
                  variant="heading_regular"
                >
                  Logout
                </Typography>
              </ListItemText>
            </ListItemButton>

            <CommonDialog
              open={open}
              title={"Logout?"}
              subtext={"Are you sure you want to logout?"}
              iconSpacing={"24px"}
              dialogSize={"xs"}
              dialogIconBg={logoutDestructiveBg}
              dialogIcon={
                <LogoutIcon
                  sx={{
                    fontSize: 28,
                    color: logoutDestructiveColor,
                  }}
                />
              }
              dialogAction={
                <React.Fragment>
                  <Button
                    variant="outlined"
                    sx={{
                      border: `1px solid ${logoutDestructiveColor}`,
                      color: logoutDestructiveColor,
                      background: theme => theme.palette.common.white,
                      fontSize: 18,
                      py: 3,
                      mr: 3,
                      "&:hover": {
                        border: `1px solid ${logoutDestructiveHoverColor}`,
                        color: logoutDestructiveHoverColor,
                        background: "#FFF5F5",
                      },
                    }}
                    onClick={handleClickClose}
                  >
                    No
                  </Button>

                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: `${logoutDestructiveColor} !important`,
                      color: "#FFFFFF",
                      fontSize: 18,
                      py: 3,
                      "&:hover": {
                        backgroundColor: `${logoutDestructiveHoverColor} !important`,
                      },
                    }}
                    onClick={onSignoutClick}
                  >
                    Yes
                  </Button>
                </React.Fragment>
              }
            />
          </Box>
        </Box>
      </List>
    </Box>
  );
};

export default LeftSidebar;
